import React from 'react'
import { Tabs } from '@/shared/ui/Tabs'
import { Card, CardContent } from '@/shared/ui/Card'
import { Pagination } from '@/shared/ui/Pagination'
import CommissionsTable from './CommissionsTable'
import { getAffiliateUplineChildren, listAffiliateUplineCommissionsByChild } from '../services/affiliateUplineService'
import type { AffiliateUplineChildOverview, AffiliateUplineCommissionRow } from '../types'

type Props = {
  parentAffiliateId: string
}

function cents(v?: number | null) {
  return `R$ ${(Number(v || 0) / 100).toFixed(2)}`
}

function getChildDisplayName(child: Pick<AffiliateUplineChildOverview, 'child_name' | 'child_code' | 'child_email'>) {
  return child.child_name || child.child_code || child.child_email || 'Afiliado sem nome'
}

export default function AffiliateUplineChildrenPanel({ parentAffiliateId }: Props) {
  const [children, setChildren] = React.useState<AffiliateUplineChildOverview[]>([])
  const [loadingChildren, setLoadingChildren] = React.useState(false)
  const [childrenError, setChildrenError] = React.useState<string | null>(null)

  const [selectedChildId, setSelectedChildId] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<AffiliateUplineCommissionRow[]>([])
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [loadingRows, setLoadingRows] = React.useState(false)
  const [rowsError, setRowsError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    async function loadChildren() {
      if (!parentAffiliateId) return
      setLoadingChildren(true)
      setChildrenError(null)
      try {
        const list = await getAffiliateUplineChildren(parentAffiliateId)
        if (cancelled) return
        setChildren(list)
        if (list.length > 0) {
          setSelectedChildId(list[0].child_affiliate_id)
          setPage(1)
        } else {
          setSelectedChildId(null)
          setRows([])
          setTotal(0)
        }
      } catch (e: any) {
        if (!cancelled) setChildrenError(e?.message || 'Falha ao carregar afiliados filhos')
      } finally {
        if (!cancelled) setLoadingChildren(false)
      }
    }
    loadChildren()
    return () => {
      cancelled = true
    }
  }, [parentAffiliateId])

  React.useEffect(() => {
    let cancelled = false
    async function loadRows() {
      if (!parentAffiliateId || !selectedChildId) return
      setLoadingRows(true)
      setRowsError(null)
      try {
        const { rows, total } = await listAffiliateUplineCommissionsByChild(parentAffiliateId, selectedChildId, page, 25)
        if (cancelled) return
        setRows(rows)
        setTotal(total)
      } catch (e: any) {
        if (!cancelled) setRowsError(e?.message || 'Falha ao carregar comissões upline')
      } finally {
        if (!cancelled) setLoadingRows(false)
      }
    }
    loadRows()
    return () => {
      cancelled = true
    }
  }, [parentAffiliateId, selectedChildId, page])

  if (!parentAffiliateId) return null

  if (!loadingChildren && children.length === 0) {
    return (
      <div className="mt-8 rounded-md border border-border/60 bg-muted/40 p-4 text-sm text-foreground/70">
        Você ainda não possui comissões upline geradas por afiliados filhos.
      </div>
    )
  }

  const tabs = children.map((c) => ({
    value: c.child_affiliate_id,
    label: getChildDisplayName(c),
  }))

  const activeChild = children.find((c) => c.child_affiliate_id === selectedChildId) || null

  return (
    <div className="mt-8 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-base font-medium">Comissões upline por afiliado filho</div>
          <p className="text-xs text-foreground/70">
            Acompanhe quanto você recebe sobre as vendas geradas pelos seus afiliados filhos (1 nível).
          </p>
        </div>
      </div>

      {childrenError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive-foreground">
          {childrenError}
        </div>
      )}

      {tabs.length > 0 && (
        <Tabs
          tabs={tabs}
          value={selectedChildId || tabs[0].value}
          onValueChange={(v) => {
            setSelectedChildId(v)
            setPage(1)
          }}
        />
      )}

      {activeChild && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 py-4">
            <div>
              <div className="text-xs text-foreground/60">Afiliado filho</div>
              <div className="text-sm font-medium">{getChildDisplayName(activeChild)}</div>
            </div>
            <div>
              <div className="text-xs text-foreground/60">Total upline (toda a história)</div>
              <div className="text-sm font-semibold">{cents(activeChild.upline_total_cents)}</div>
            </div>
            <div>
              <div className="text-xs text-foreground/60">Upline nos últimos 30 dias</div>
              <div className="text-sm font-semibold">{cents(activeChild.upline_30d_cents)}</div>
            </div>
            <div>
              <div className="text-xs text-foreground/60">Número de comissões upline</div>
              <div className="text-sm font-semibold">{activeChild.upline_commissions_count}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {rowsError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive-foreground">
          {rowsError}
        </div>
      )}

      {loadingRows ? (
        <div className="text-sm text-foreground/70">Carregando comissões upline...</div>
      ) : (
        <>
          <CommissionsTable rows={rows as any} accountNames={{}} />
          <div className="mt-4 flex justify-end">
            <Pagination page={page} total={Math.max(1, Math.ceil(total / 25))} onChange={setPage} />
          </div>
        </>
      )}
    </div>
  )
}

